import axios from "axios";

const BACKEND_URL =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    timeout: 15000,
});

// ── Helpers ──────────────────────────────────────────────────────────────────
// Returns an Error carrying the HTTP status so callers can special-case it
// (e.g. 409 = email already on the waitlist → friendly "we'll reach out" toast).
const handleError = (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const msg =
        (Array.isArray(detail) ? detail[0]?.msg : detail) ||
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. Please try again.";
    const e = new Error(msg);
    e.status = status;
    e.alreadyRegistered = status === 409;
    return e;
};

// ── OTP (mobile verification via Plivo) ───────────────────────────────────────
export const sendMobileOtp = async (mobileNumber) => {
    try {
        const res = await api.post("/otp/send", { mobile_number: mobileNumber });
        return res.data; // { session_uuid, message }
    } catch (err) {
        throw handleError(err);
    }
};

export const verifyMobileOtp = async (sessionUuid, otp) => {
    try {
        const res = await api.post("/otp/verify", {
            session_uuid: sessionUuid,
            otp,
        });
        return res.data; // { verified, message }
    } catch (err) {
        throw handleError(err);
    }
};

// ── Waitlist ─────────────────────────────────────────────────────────────────
export const submitStudentWaitlist = async (data) => {
    try {
        const res = await api.post("/waitlist/students", data);
        return res.data;
    } catch (err) {
        throw handleError(err);
    }
};

export const submitUniversityWaitlist = async (data) => {
    try {
        const res = await api.post("/waitlist/universities", data);
        return res.data;
    } catch (err) {
        throw handleError(err);
    }
};

export const submitRecruiterWaitlist = async (data) => {
    try {
        const res = await api.post("/waitlist/recruiters", data);
        return res.data;
    } catch (err) {
        throw handleError(err);
    }
};

// ── Lead Capture (modal) — Maps to existing waitlist endpoints ────────────────
export const submitLead = async (data) => {
    try {
        let res;
        if (data.role === "student") {
            res = await api.post("/waitlist/students", {
                full_name: data.studentName || "",
                email: data.studentEmail || "",
                mobile_number: data.mobile,
                city_location: data.studentState || "",
                preferred_language: data.studentLang || "",
                discovery_source: "Lead Modal",
            });
        } else if (data.role === "university") {
            const looking_for = data.universityInterest
                ? data.universityInterest === "Placement Track"
                    ? ["Placement Track"]
                    : [data.universityInterest]
                : ["Four Year CS Programme"];
            res = await api.post("/waitlist/universities", {
                institution_name: data.universityName || "",
                institution_type: "Engineering College",
                year_of_establishment: "Not Specified",
                official_email: `${data.mobile}@lead.aiprof.in`,
                mobile_number: data.mobile,
                city: "",
                state: "",
                contact_person_name: data.contactName || "",
                designation_role: data.contactPosition || "",
                department: data.contactDepartment || "",
                // Backend expects a string here; modal doesn't collect a count.
                tech_students_count: "Not Specified",
                has_placement_cell: "Yes",
                partnered_with_edtech: "No",
                conducts_campus_drives: "Yes",
                current_placement_rate: "",
                looking_for: looking_for,
                deployment_timeline: "Just Exploring",
            });
        }
        return res?.data;
    } catch (err) {
        throw handleError(err);
    }
};

// ── Quiz ─────────────────────────────────────────────────────────────────────
export const analyzeQuiz = async (data) => {
    try {
        const res = await api.post("/quiz/analyze", data);
        return res.data;
    } catch (err) {
        throw handleError(err);
    }
};

// ── Stats ────────────────────────────────────────────────────────────────────
export const fetchStats = async () => {
    try {
        const res = await api.get("/stats");
        return res.data;
    } catch (err) {
        throw handleError(err);
    }
};

// ── BTech Enrollment Payment ──────────────────────────────────────────────────
// ── Brochure lead capture ─────────────────────────────────────────────────────
export const captureBrochureLead = async (data) => {
    try {
        const res = await api.post("/brochure/lead", data);
        return res.data;
    } catch (err) {
        throw handleError(err);
    }
};

// ── SMRU /RFC partner page leads (guidance + brochure) ────────────────────────
// Stored server-side in the `smru_students` collection.
export const submitSmruLead = async (data) => {
    try {
        const res = await api.post("/smru/students", data);
        return res.data;
    } catch (err) {
        throw handleError(err);
    }
};

export const createBTechOrder = async (data) => {
    try {
        const res = await api.post("/payment/btech/create-order", data);
        return res.data; // { order_id, amount, currency, enrollment_id, key_id }
    } catch (err) {
        throw handleError(err);
    }
};

export const verifyBTechPayment = async (data) => {
    try {
        const res = await api.post("/payment/btech/verify", data);
        return res.data; // { success, message, enrollment_id }
    } catch (err) {
        throw handleError(err);
    }
};
